import React, { useEffect, useState, useRef } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { fetchApiHub } from 'frontastic';
import { AgGridReact } from 'ag-grid-react'; 
import { AllCommunityModule, ModuleRegistry, ValueFormatterParams, ValueParserParams ,themeMaterial } from 'ag-grid-community';
import "ag-grid-community/styles/ag-theme-alpine.css"; // thème Alpine

interface Props {
  bu?: any;
  invoiceType?: string
}
ModuleRegistry.registerModules([ AllCommunityModule ]);

const SOATab = ({ bu, invoiceType }: Props) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const [results, setResults] = useState([]);
  const [linesForResults, setLineForResults] = useState([]);
  const [diff, setDiff] = useState(0);
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    fetchApiHub(`/action/bic/getSOA`, {method: 'GET'}).then((result) => {
      const results = result[0]?.value?.bic_soa_financial_reports;
      setResults(results ?? []);
      setLineForResults(results ?? []);
      const detectedCurrency = results?.['customer_invoices']?.[0]?.currency ?? results?.['supplier_invoices']?.[0]?.currency ?? 'USD';
      setCurrency(detectedCurrency);
      let diff = 0;
      let source = results['customer_invoices'] ?? [];
      source.map((invoice) => (
        diff += invoice['invoice_amount']
      ))
      source = results['supplier_invoices'] ?? [];
      source.map((invoice) => (
        diff -= invoice['invoice_amount'] ?? 0
      ))
      setDiff(diff);
    })
  }, []);
  // Column Definitions: Defines the columns to be displayed.
  const [colDefsForCustomerInvoices, setColDefsForCustomerInvoices] = useState([
      { field: "invoice_number", headerName: "#", filter: true },
      { field: "invoice_type", headerName: "Type", flex: 0, sortable: false, width: 90},
      { field: "invoice_date", headerName: "Date", filter: true },
      { field: "due_date", headerName: "Due date" },
      { field: "RPSFX", headerName: 'RPSFX', filter: true },
      { field: "RPKCO", headerName: 'RPKCO', filter: true },
      { field: "invoice_amount", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Amount' },
      { field: "invoice_open_amount", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Open amount' },
      { field: "current", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Current' },
      { field: "late_1-30", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Late 1-30'},
      { field: "late_31-60", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Late 31-60'},
      { field: "late_61-90", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Late 61-90'},
      { field: "late_91-120", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Late 91-120'},
      { field: "late_120", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Late 120'},
  ]);

  const [colDefsForSupplierInvoices, setColDefsForSupplierInvoices] = useState([
      { field: "invoice_number", headerName: "#", filter: true },
      { field: "invoice_type", flex:0 , width:90, headerName: "Type" },
      { field: "supplier_name", flex:0 , width:200, headerName: "Supplier name", filter: true},
      { field: "supplier_invoice",flex:0, width: 125, headerName: "Invoice"},
      { field: "invoice_date", headerName: 'Date'},
      { field: "RPSFX", headerName: 'RPSFX' },
      { field: "RPKCO", headerName: 'RPKCO' },
      { field: "due_date", headerName: 'Due date'},
      { field: "invoice_amount", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Amount', filter: true },
      { field: "invoice_open_amount", type: 'currency', valueFormatter: currencyFormatter, headerName: 'Open amount', filter: true },
  ]);

  // Column Definitions: Defines the columns to be displayed.
  const [colDefsForCustomerTotalsInvoices, setColDefsForCustomerTotalsInvoices] = useState([
      {},
      {flex: 0, width: 90},
      {},
      {},
      {},
      { field: "total_label" },
      { field: "total_amount", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_open_amount", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_current", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_late_1_30", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_late_31_60", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_late_61_90", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_late_91_120", type: 'currency', valueFormatter: currencyFormatter },
      { field: "total_late_120", type: 'currency', valueFormatter: currencyFormatter },
  ]);

  const [colDefsForSupplierTotalInvoices, setColDefsForSupplierTotalsInvoices] = useState([
      {},
      {flex: 0, width: 90},
      {flex: 0, width: 185},
      {flex: 0, width: 125},
      {},
      {},
      {},
      { field: "total_label", width: 125, headerName: 'Due date', resizable: false, flex: 1},
      { field: "total_amount", width: 125, type: 'currency', valueFormatter: currencyFormatter, headerName: 'Amount', resizable: false, filter: true, flex: 1 },
      { field: "total_open_amount", width: 150, type: 'currency', valueFormatter: currencyFormatter, headerName: 'Open amount', resizable: false, filter: true, flex: 1 },
  ]);

  const myTheme = themeMaterial.withParams({
    headerBackgroundColor: 'rgb(75, 85, 99)',
    headerTextColor: 'white',
    wrapperBorder: {style: 'none'},
    footerRowBorder: true,
    rowBorder: true,
    columnBorder: true,
    oddRowBackgroundColor: 'rgb(209, 213, 219)',
  });

  const totalsTheme = myTheme.withParams({
    wrapperBorder: false,
    footerRowBorder: false,
    borderWidth: 0,
    headerHeight: 0
  });

  function getTotalsData(invoiceType: string) {
    if ("customer_invoices" === invoiceType) {
      const totalAmount = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row.invoice_amount, 0);
      const totalOpenAmount = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row.invoice_open_amount, 0);
      const totalCurrent = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row.current, 0);
      const totalLate1 = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row['late_1-30'], 0);
      const totalLate31 = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row['late_31-60'], 0);
      const totalLate61 = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row['late_61-90'], 0);
      const totalLate91 = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row['late_91-120'], 0);
      const totalLate120 = linesForResults['customer_invoices']?.reduce((acc, row) => acc + row['late_120'], 0);

      return [{
        'total_amount': totalAmount,
        'total_open_amount': totalOpenAmount,
        'total_current': totalCurrent,
        'total_label': 'Totals',
        'total_late_1_30': totalLate1,
        'total_late_31_60': totalLate31,
        'total_late_61_90': totalLate61,
        'total_late_91_120': totalLate91,
        'total_late_120': totalLate120,
        'currency': currency,
      }];
    } else {
      const totalAmount = linesForResults['supplier_invoices']?.reduce((acc, row) => acc + row.invoice_amount, 0);
      const totalOpenAmount = linesForResults['supplier_invoices']?.reduce((acc, row) => acc + row.invoice_open_amount, 0);

      return [{'total_amount': totalAmount, 'total_open_amount': totalOpenAmount, 'total_label': 'Totals', 'currency': currency}];
    }
  }

  function currencyFormatter(params: ValueFormatterParams) {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: params.context?.currency ?? params.data?.currency ?? 'USD',
      currencyDisplay: 'narrowSymbol',
    });
    const value = params.value;
    if (isNaN(value)) {
      return "";
    }

    return formatter.format(value);
  }

  const onFilterChanged = (params: any) => {
    const filteredData: any[] = [];
    params.api.forEachNodeAfterFilter((node: any) => {
      filteredData.push(node.data);
    });
    setLineForResults(filteredData);
  };

  return (
    <div>
      <div>
      
      <div>
        {results['customer_invoices'] && (
          <AgGridReact
              key={'lines_customer_invoices'}
              rowData={results['customer_invoices']}
              context={{ currency }}
              columnDefs={colDefsForCustomerInvoices}
              theme={myTheme}
              domLayout='print'
              onFilterChanged={onFilterChanged}
              defaultColDef={{
                width: 125,
                autoHeight: true,
                resizable: false,
                flex: 1,
                cellStyle: { whiteSpace: "pre-wrap"},
              }}
          />
        )}
      </div>
      <div style={{height: 200}}>
        {results['customer_invoices'] && (
          <AgGridReact
              key={'totals_customer_invoices'}
              rowData={getTotalsData('customer_invoices')}
              context={{ currency }}
              columnDefs={colDefsForCustomerTotalsInvoices}
              theme={totalsTheme}
              domLayout='print'
              defaultColDef={{
                width: 125,
                autoHeight: true,
                resizable: false,
                flex: 1,
                cellStyle: { whiteSpace: "pre-wrap"},
              }}
          />
        )}
      </div>





      <div style={{marginLeft: '425px'}}>
        {results['supplier_invoices'] && (
          <AgGridReact
              key={'lines_supplier_invoices'}
              rowData={results['supplier_invoices']}
              context={{ currency }}
              columnDefs={colDefsForSupplierInvoices}
              theme={myTheme}
              domLayout='print'
              onFilterChanged={onFilterChanged}
              defaultColDef={{
                width: 125,
                autoHeight: true,
                resizable: false,
                flex: 1,
                cellStyle: { whiteSpace: "pre-wrap"},
              }}
          />
        )}
      </div>
      <div style={{height: 100, marginLeft: '425px'}}>
        {results['supplier_invoices'] && (
        <AgGridReact
            key={'totals_supplier_invoices'}
            rowData={getTotalsData('supplier_invoices')}
            context={{ currency }}
            columnDefs={colDefsForSupplierTotalInvoices}
            theme={totalsTheme}
            domLayout='print'
            defaultColDef={{
              width: 125,
              autoHeight: true,
              resizable: false,
              flex: 1,
              cellStyle: { whiteSpace: "pre-wrap"},
            }}
        />
          )}
      </div>


      <div
        role="row"
        row-index={0}
        row-id={0}
        tabIndex={-1}
        className="ag-row-even ag-row-no-focus ag-row ag-row-level-0 ag-row-position-relative ag-row-first ag-row-last"
        aria-rowindex={2}
        style={{ height: 48, marginLeft: '440px' }}
      >
        <div
          role="gridcell"
          col-id={0}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height ag-column-first"
          aria-colindex={1}
          style={{ left: 0, width: 129, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-0-714" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={1}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={2}
          style={{ left: 129, width: 90, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-1-715" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={2}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={3}
          style={{ left: 219, width: 185, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-2-716" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={3}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={4}
          style={{ left: 404, width: 125, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-3-717" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={4}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={5}
          style={{ left: 529, width: 128, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-4-718" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={5}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={6}
          style={{ left: 657, width: 129, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-5-719" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id={6}
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={7}
          style={{ left: 786, width: 128, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span role="presentation" id="cell-6-720" className="ag-cell-value" />
          </div>
        </div>
        <div
          role="gridcell"
          col-id="total_label"
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={8}
          style={{ left: 914, width: 129, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span
              role="presentation"
              id="cell-total_label-721"
              className="ag-cell-value"
            >
              Amount due as of today
            </span>
          </div>
        </div>
        <div
          role="gridcell"
          col-id="total_amount"
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height"
          aria-colindex={9}
          style={{ left: 1043, width: 128, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span
              role="presentation"
              id="cell-total_amount-722"
              className="ag-cell-value"
            >
            </span>
          </div>
        </div>
        <div
          role="gridcell"
          col-id="total_open_amount"
          tabIndex={-1}
          className="ag-cell ag-cell-not-inline-editing ag-cell-auto-height ag-column-last"
          aria-colindex={10}
          style={{ left: 1171, width: 129, whiteSpace: "pre-wrap" }}
        >
          <div className="ag-cell-wrapper" role="presentation">
            <span
              role="presentation"
              id="cell-total_open_amount-723"
              className="ag-cell-value"
            >
              {diff.toLocaleString('en-US', { style: 'currency', currency: currency })}
            </span>
          </div>
        </div>
      </div>
    </div>
    </div>
  )
};

export default SOATab;
